import * as React from "react";
import { Dropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import ActionButton from "../../../repertoire/components/actionButton/ActionButton";
import { WORK_SOURCE_LOOKUP } from "../../../lookup/Consts";
import { CREATE_MANUAL_JOB_TITLE, CLEANUP_INTRAY_CUE_JOB_TYPE } from "../../Consts";
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";
import { IScheduledJobState } from "../../../redux/types/IScheduledJobState";

const SCHEDULE = "CIC_Schedule";

export interface CleanupIntrayCueParametersProps {
    triggerManualJob: (manualJob: ITriggerManualJob) => any;
    createNewJob: () => any;
    setManualJobParameters: (parameters: ITriggerManualJob) => void;
    lookupValues: ILookupDictionary;
    upsertScheduledJobAsync: (schedule: IScheduledJobState) => any;
    getScheduledJob: (jobType: string, jobParameters: string) => void;
    scheduledJob?: IScheduledJobState;
    scheduledJobSet?: (value: boolean) => void;
    onError?: (errorMessage: string) => void;
}

export const CleanupIntrayCueParameters = ({
    triggerManualJob,
    createNewJob,
    setManualJobParameters,
    lookupValues,
    upsertScheduledJobAsync,
    getScheduledJob,
    scheduledJob,
    scheduledJobSet,
    onError
}: CleanupIntrayCueParametersProps) => {
    const [sources, setSources] = React.useState<string[]>([]);
    const [scheduledValue, setScheduledValue] = React.useState("");
    const [cronError, setCronError] = React.useState("");
    const [loading, setLoading] = React.useState(false);

    const sourceOptions = React.useMemo<IDropdownOption[]>(() => {
        if (!lookupValues || !lookupValues[WORK_SOURCE_LOOKUP]) {
            return [];
        }

        return lookupValues[WORK_SOURCE_LOOKUP].lookups
            .filter((item) => item.code && item.code !== "NULL")
            .map((item) => ({
                key: item.code,
                text: item.description || item.code,
                data: item.code
            }))
            .sort((a, b) => String(a.text).localeCompare(String(b.text)));
    }, [lookupValues]);

    const isValidCron = (cron: string) => {
        const parts = cron.trim().split(/\s+/);
        if (parts.length !== 5 && parts.length !== 6) {
            return false;
        }

        const validators = [
            { regex: /^(\*|([0-5]?\d)(\/\d+)?|\d+(,\d+)*|\*\/\d+)$/ },
            { regex: /^(\*|([0-5]?\d)(\/\d+)?|\d+(,\d+)*|\*\/\d+)$/ },
            { regex: /^(\*|([01]?\d|2[0-3])(\/\d+)?|\d+(,\d+)*|\*\/\d+)$/ },
            { regex: /^(\*|([1-9]|[12]\d|3[01])(\/\d+)?|\d+(,\d+)*|\*\/\d+)$/ },
            { regex: /^(\*|(1[0-2]|0?[1-9])(\/\d+)?|\d+(,\d+)*|\*\/\d+)$/ },
            { regex: /^(\*|[0-6](\/\d+)?|\d+(,\d+)*|\*\/\d+)$/ }
        ];

        const offset = parts.length === 5 ? 1 : 0;

        return parts.every((part, i) => validators[i + offset].regex.test(part));
    };

    const getParameters = React.useCallback(() => {
        const selectedSources = [...sources].sort((a, b) => a.localeCompare(b));
        return JSON.stringify({ sources: selectedSources });
    }, [sources]);

    React.useEffect(() => {
        if (sources.length > 0) {
            getScheduledJob(CLEANUP_INTRAY_CUE_JOB_TYPE, getParameters());
        } else {
            setScheduledValue("");
        }
    }, [sources, getScheduledJob, getParameters]);

    React.useEffect(() => {
        if (scheduledJob && typeof scheduledJob.cronSchedule === "string") {
            setScheduledValue(scheduledJob.cronSchedule);
        }
    }, [scheduledJob]);

    const handleSourceChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (!option) {
            return;
        }

        const selectedKey = String(option.key);
        setSources((currentSources) => {
            if (option.selected) {
                if (currentSources.includes(selectedKey)) {
                    return currentSources;
                }
                return [...currentSources, selectedKey];
            }

            return currentSources.filter((source) => source !== selectedKey);
        });

        setScheduledValue("");
        setCronError("");
    };

    const handleScheduleChange = (value: string | number | boolean) => {
        setScheduledValue(String(value || ""));
        setCronError("");
    };

    const isValidJob = () => sources.length > 0;

    const setParamsAndTriggerManualJob = () => {
        const manualJobParameters: ITriggerManualJob = {
            jobType: CLEANUP_INTRAY_CUE_JOB_TYPE,
            jobParameters: getParameters()
        };

        setManualJobParameters(manualJobParameters);
        return triggerManualJob(manualJobParameters);
    };

    const onClick = async () => {
        setLoading(true);
        try {
            const result = await Promise.resolve(setParamsAndTriggerManualJob());
            if (result) {
                scheduledJobSet?.(true);
                createNewJob();
            }
        } catch (err: any) {
            onError?.(err?.message || err?.toString() || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const setSchedule = async () => {
        if (!isValidCron(scheduledValue)) {
            setCronError("Please enter a valid Schedule expression.");
            return;
        }

        setLoading(true);
        try {
            const schedule: IScheduledJobState = {
                scheduledJobID: 0,
                type: CLEANUP_INTRAY_CUE_JOB_TYPE,
                cronSchedule: scheduledValue,
                parameters: getParameters(),
                errorList: [],
                lastRunDate: null
            };

            await Promise.resolve(upsertScheduledJobAsync(schedule));
            scheduledJobSet?.(true);
            createNewJob();
        } catch (err: any) {
            onError?.(err?.message || err?.toString() || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const dismissAlert = () => {
        setCronError("");
    };

    return (
        <>
            <div className="row">
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <label>Sources</label>
                    <Dropdown
                        selectedKeys={sources}
                        options={sourceOptions}
                        multiSelect={true}
                        onChange={handleSourceChange}
                        placeholder={""}
                        required={true}
                    />
                </div>
            </div>

            <div className="row">
                <div className="form-group col-md-4 col-sm-6 col-xs-12">
                    <TextDataInput
                        label={"Schedule"}
                        fieldName={SCHEDULE}
                        changeData={handleScheduleChange}
                        value={scheduledValue}
                        readOnly={!isValidJob()}
                        isHidden={false}
                        isMandatory={false}
                    />
                </div>
                <div style={{ marginTop: 12 }} className="form-group col-md-2 col-sm-4 col-xs-12">
                    <ActionButton buttonText={"Set"} buttonAction={setSchedule} disabled={!isValidJob() || loading} />
                </div>
            </div>

            {cronError && cronError !== "" && (
                <div className="alert alert-danger alert-dismissible">
                    <span>{cronError}</span>
                    <button type="button" className="close" aria-label="Close" onClick={dismissAlert}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            )}

            <div className="row">
                <div className="form-group col-lg-4 col-md-4 col-sm-6 col-xs-12">
                    <ActionButton buttonText={"Cancel"} buttonAction={createNewJob} className={"button btn-defaultSecondary"} />
                </div>
                <div className="form-group col-lg-4 col-lg-push-4 col-md-4 col-md-push-4 col-sm-6 col-sm-push-0 col-xs-12 col-xs-push-0">
                    <ActionButton buttonText={CREATE_MANUAL_JOB_TITLE} buttonAction={onClick} disabled={!isValidJob() || loading} />
                </div>
            </div>
        </>
    );
};

export default CleanupIntrayCueParameters;
